<template>
    <div class="asset-change">
        <div class="asset-change__head">
            <h5 class="asset-change__title">내 자산 · 지난달 대비</h5>
            <span class="asset-change__note">
                지금 로그인한 계정 기준 · 판매장부 「자산현황」과 같은 계산
                <template v-if="mom"> · {{ shortDate(mom.date) }} 기준, {{ shortDate(mom.baseDate) }} 대비</template>
            </span>
        </div>

        <div v-if="state === 'loading'" class="asset-change__status" role="status" aria-live="polite">
            <span class="asset-change__spinner" aria-hidden="true"></span>
            자산 현황을 계산하는 중…
        </div>

        <div v-else-if="state === 'error'" class="asset-change__status">
            자산 현황을 불러오지 못했습니다. 잠시 뒤 다시 시도해 주세요.
            <button type="button" class="btn btn-outline-primary btn-sm ml-2" @click="load">다시 시도</button>
        </div>

        <div v-else-if="state === 'locked'" class="asset-change__status">
            판매장부가 잠겨 있어 금액을 숨겼습니다.
            <router-link :to="{ name: ledgerRouteName }" class="ml-1">판매장부에서 잠금 풀기 ›</router-link>
        </div>

        <div v-else-if="state === 'ready'" class="asset-change__tiles">
            <div v-for="tile in tiles" :key="tile.key" class="asset-change__tile">
                <span class="asset-change__label">{{ tile.label }}</span>

                <template v-if="tile.empty">
                    <span class="asset-change__empty">{{ tile.empty }}</span>
                    <router-link v-if="tile.emptyLink" :to="{ name: ledgerRouteName }" class="asset-change__link">
                        판매장부로 이동 ›
                    </router-link>
                </template>

                <template v-else>
                    <!-- 핵심은 증감이다. 색만으로 구분하지 않도록 ▲▼ 와 부호를 같이 붙인다 -->
                    <span class="asset-change__delta" :class="'asset-change__delta--' + tile.tone">
                        <span aria-hidden="true">{{ tile.arrow }}</span>
                        {{ tile.big }}
                    </span>
                    <span v-if="tile.small" class="asset-change__amount" :class="'asset-change__amount--' + tile.tone">
                        {{ tile.small }}
                    </span>
                    <span class="asset-change__now">
                        지금 {{ won(tile.now) }}
                        <template v-if="tile.base !== null"> · {{ shortDate(mom.baseDate) }} {{ won(tile.base) }}</template>
                    </span>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
/**
 * 대시보드 「현금자산 · 재고자산」 지난달 대비 타일.
 *
 * 계산은 판매장부 「자산현황」 차트와 **같은 함수**(utils/assetSeries.js)를 부르고,
 * 재료도 판매장부가 부르는 것과 같은 API·같은 캐시에서 받는다 — 두 화면 숫자가 갈리면 안 된다.
 * 판매장부는 연도를 고를 수 있는데, 첫 화면은 올해다. 여기서도 올해 기준으로 계산한다.
 */
import axios from 'axios';
import ledgerCache from '@/services/ledgerCache';
import { buildAssetSeries, monthOverMonth } from '@/utils/assetSeries';

export default {
    name: 'AssetChangeTiles',
    props: {
        // 판매장부 요약 연도 (판매장부 첫 화면과 같은 올해가 기본)
        year: { type: String, default: () => String(new Date().getFullYear()) },
        // 안내 링크가 가리킬 판매장부 라우트 이름
        ledgerRouteName: { type: String, default: 'growth.salesledger' }
    },
    data() {
        return {
            state: 'loading',   // loading | ready | locked | error
            mom: null,
            hasSetting: false
        };
    },
    computed: {
        tiles() {
            if (!this.mom) return [];
            const cash = this.mom.cash;
            const stock = this.mom.stock;
            return [
                this.tileOf('cash', '현금자산', cash,
                    !this.hasSetting ? '판매장부에서 기준 통장잔고를 입력하면 표시됩니다' : '비교 데이터 없음',
                    !this.hasSetting),
                this.tileOf('stock', '재고자산', stock, '비교 데이터 없음', false)
            ];
        }
    },
    mounted() {
        this.load();
    },
    methods: {
        async load() {
            this.state = 'loading';
            this.mom = null;
            try {
                // 잠금부터 본다. 잠긴 세션에서 캐시를 꺼내 쓰면 잠금이 숨기려던 숫자가 새어 나간다
                const lockRes = await axios.post('/api/growth/getLedgerLock', {});
                const locked = lockRes.data?.res === 'success' && !!lockRes.data.locked && !lockRes.data.unlocked;
                if (locked) {
                    this.state = 'locked';
                    return;
                }

                const [days, fixedRes, costRes, settingRes, coverageRes, inboundRes, scheduleRes, purchaseLots] = await Promise.all([
                    this.loadDailySummary(),
                    axios.post('/api/growth/getFixedCosts', {}),
                    axios.post('/api/growth/getSalesLedgerCosts', {}),
                    axios.post('/api/growth/getLedgerSetting', {}),
                    axios.post('/api/getInventoryCoverage', {}),
                    axios.post('/api/growth/getInboundHistory', {}),
                    axios.post('/api/growth/getSettlementSchedules', {}),
                    this.loadPurchaseLots()
                ]);
                if (days === 'locked') {
                    this.state = 'locked';
                    return;
                }

                const setting = settingRes.data?.res === 'success' ? settingRes.data.setting : null;
                this.hasSetting = !!(setting && setting.baseCashDate);
                const model = buildAssetSeries({
                    dailyData: days,
                    fixedCosts: fixedRes.data?.res === 'success' ? (fixedRes.data.fixedCosts || []) : [],
                    productCosts: costRes.data?.res === 'success' ? (costRes.data.costs || []) : [],
                    setting,
                    coverage: coverageRes.data?.success ? (coverageRes.data.coverage || []) : [],
                    inbound: inboundRes.data?.res === 'success' ? (inboundRes.data.inbound || []) : [],
                    schedules: scheduleRes.data?.res === 'success' ? (scheduleRes.data.schedules || []) : [],
                    purchaseLots
                });
                this.mom = monthOverMonth(model) || {
                    date: '', baseDate: '',
                    cash: { now: 0, base: null, diff: null, rate: null },
                    stock: { now: 0, base: null, diff: null, rate: null }
                };
                this.state = 'ready';
            } catch (e) {
                console.error('[대시보드] 자산 타일 로딩 실패', e);
                this.state = 'error';
            }
        },

        // 판매장부 요약(일별 집계). 판매장부와 같은 캐시 키·같은 변경시각 판정을 쓴다 —
        // 판매장부를 먼저 열었으면 바로 꺼내 쓰고, 여기서 받은 것은 판매장부도 그대로 쓴다
        async loadDailySummary() {
            const y = this.year;
            const scope = this.$store.state.user?._id || '';
            const cacheKey = 'summary-' + y;
            let stamp = null;
            try {
                const stampRes = await axios.post('/api/growth/getLedgerStamp', {});
                if (stampRes.data?.res === 'success') stamp = stampRes.data.stamp || '';
            } catch (e) {
                console.error('[대시보드] 장부 변경시각 확인 실패', e);
            }

            if (stamp !== null) {
                const cached = await ledgerCache.getMonth(scope, cacheKey, stamp);
                if (cached && Array.isArray(cached.months)) return cached.days || [];
            }

            const res = await axios.post('/api/growth/getLedgerMonthlySummary', { year: y });
            if (res.data?.res === 'locked') return 'locked';
            if (res.data?.res !== 'success') throw new Error('요약 조회 실패: ' + (res.data?.alert || ''));
            if (stamp !== null) {
                await ledgerCache.putMonth(scope, cacheKey, {
                    months: res.data.months || [],
                    days: res.data.days || [],
                    missingPurchasePrice: res.data.missingPurchasePrice || [],
                    missingPurchasePriceCount: res.data.missingPurchasePriceCount || 0,
                    missingPurchasePriceAmount: res.data.missingPurchasePriceAmount || 0
                }, stamp);
            }
            return res.data.days || [];
        },

        // 상품별 매입 lot — 판매장부 purchaseLots 와 같은 규칙으로 고른다.
        // 상품DB 가 이미 스토어에 있으면 그걸 쓰고, 없으면 판매장부와 같은 호출로 받아 스토어에 둔다
        // (그러면 판매장부를 열 때 다시 받지 않는다)
        async loadPurchaseLots() {
            let growthdb = this.$store.state.growthdb;
            if (!growthdb || !growthdb.length) {
                const res = await axios.post('/api/getGrowthDBV2');
                growthdb = res.data?.growthdb || [];
                if (growthdb.length) this.$store.commit('growthdbupdate', growthdb);
            }
            const out = [];
            (growthdb || []).forEach(g => {
                if (!g || g.vendorItemId == null || !Array.isArray(g.매입정보)) return;
                if (!g.매입정보.some(r => r && r.입고ID && (parseFloat(r.수량) > 0))) return;
                out.push({ vendorItemId: g.vendorItemId, 매입정보: g.매입정보 });
            });
            return out;
        },

        tileOf(key, label, v, emptyText, emptyLink) {
            if (!v || v.diff === null) {
                return { key, label, empty: emptyText, emptyLink };
            }
            const up = v.diff > 0;
            const down = v.diff < 0;
            const sign = up ? '+' : (down ? '−' : '');
            const diffText = sign + Math.round(Math.abs(v.diff)).toLocaleString('ko-KR') + '원';
            return {
                key,
                label,
                empty: '',
                now: v.now,
                base: v.base,
                tone: up ? 'up' : (down ? 'down' : 'flat'),
                arrow: up ? '▲' : (down ? '▼' : '■'),
                // 지난달 값이 0 이하면 % 는 뜻이 없다 — 그때는 금액을 크게 보인다
                big: v.rate === null ? diffText : sign + (Math.abs(v.rate) * 100).toFixed(1) + '%',
                small: v.rate === null ? '' : diffText
            };
        },
        won(v) {
            return Math.round(Number(v) || 0).toLocaleString('ko-KR') + '원';
        },
        // 2026-08-31 → 8/31
        shortDate(d) {
            if (!d) return '';
            return parseInt(d.slice(5, 7), 10) + '/' + parseInt(d.slice(8, 10), 10);
        }
    }
};
</script>

<style lang="scss" scoped>
.asset-change {
    margin-bottom: 1rem;

    &__head {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 0.6rem;
        margin-bottom: 0.6rem;
    }

    &__title {
        margin: 0;
        font-weight: 700;
        color: #1b2a3a;
    }

    &__note {
        font-size: 0.8rem;
        color: #7f8c9a;
    }

    &__status {
        padding: 1rem 1.2rem;
        border: 1px solid #e3e8ee;
        border-radius: 0.5rem;
        background: #fff;
        color: #5a6b7b;
    }

    &__spinner {
        display: inline-block;
        width: 14px;
        height: 14px;
        margin-right: 0.4rem;
        vertical-align: -2px;
        border: 2px solid #cfd8e0;
        border-top-color: #2b6cb0;
        border-radius: 50%;
        animation: asset-change-spin 0.8s linear infinite;
    }

    &__tiles {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
        gap: 1rem;
    }

    &__tile {
        display: flex;
        flex-direction: column;
        padding: 1.2rem 1.4rem;
        border: 1px solid #e3e8ee;
        border-radius: 0.6rem;
        background: #fff;
        min-height: 150px;
    }

    &__label {
        font-size: 1rem;
        font-weight: 700;
        color: #5a6b7b;
    }

    &__delta {
        margin-top: 0.3rem;
        font-size: 2.6rem;
        font-weight: 800;
        line-height: 1.15;
        font-variant-numeric: tabular-nums;

        &--up { color: #1e874b; }
        &--down { color: #c0392b; }
        &--flat { color: #5a6b7b; }
    }

    &__amount {
        font-size: 1.3rem;
        font-weight: 700;
        font-variant-numeric: tabular-nums;

        &--up { color: #1e874b; }
        &--down { color: #c0392b; }
        &--flat { color: #5a6b7b; }
    }

    &__now {
        margin-top: 0.5rem;
        font-size: 0.85rem;
        color: #7f8c9a;
        font-variant-numeric: tabular-nums;
    }

    &__empty {
        margin-top: 0.8rem;
        font-size: 1rem;
        color: #7f8c9a;
    }

    &__link {
        margin-top: 0.4rem;
        font-size: 0.9rem;
    }
}

@keyframes asset-change-spin {
    to { transform: rotate(360deg); }
}
</style>
